// App theme cycle — one button steps through Black → colors → White → camo set → back to Black.
// Each step remaps the brand accent (--ac-rgb) and the page background (--bg-rgb).
// White flips to a light theme; camo entries paint a woodland pattern via --camo-* vars.
export const ACCENTS = [
  { id: "black", label: "Black", rgb: "0 255 194", bg: "3 3 5", hex: "#0A0A0F" },
  { id: "blue", label: "Blue", rgb: "0 170 255", bg: "3 26 56", hex: "#00AAFF" },
  { id: "turquoise", label: "Green", rgb: "0 255 194", bg: "2 48 40", hex: "#00FFC2" },
  { id: "orange", label: "Orange", rgb: "255 122 0", bg: "48 22 2", hex: "#FF7A00" },
  { id: "yellow", label: "Yellow", rgb: "255 221 0", bg: "46 38 2", hex: "#FFDD00" },
  { id: "pink", label: "Pink", rgb: "255 32 141", bg: "50 6 28", hex: "#FF208D" },
  { id: "red", label: "Red", rgb: "255 40 55", bg: "46 4 8", hex: "#FF2A37" },
  { id: "white", label: "White", rgb: "0 176 138", bg: "244 245 250", hex: "#FFFFFF", light: true },
  { id: "camo", label: "Green Camo", rgb: "154 179 90", bg: "30 36 22", hex: "#6E7A3E",
    camo: { base: "#2f3720", a: "#1c2113", b: "#5c6438", c: "#4a5330", d: "#262d18" } },
  { id: "camo-purple", label: "Purple Camo", rgb: "180 130 255", bg: "36 26 51", hex: "#8B5CF6",
    camo: { base: "#241a33", a: "#150f22", b: "#4a3170", c: "#3a2557", d: "#1c1229" } },
  { id: "camo-burgundy", label: "Burgundy Camo", rgb: "224 90 110", bg: "43 18 24", hex: "#8E2438",
    camo: { base: "#2b1218", a: "#1a0a0f", b: "#5c2432", c: "#48202c", d: "#210d13" } },
  { id: "camo-desert", label: "Desert Camo", rgb: "224 190 120", bg: "58 48 32", hex: "#C2A36B",
    camo: { base: "#3a3020", a: "#2a2214", b: "#6e5c38", c: "#574830", d: "#2e2618" } },
  { id: "robot-cyber", label: "Cyber Grid", rgb: "0 200 255", bg: "5 13 24", hex: "#00C8FF",
    robot: { base: "#050d18", line: "rgba(0,180,255,0.05)", line2: "rgba(0,180,255,0.12)", glow: "rgba(0,210,255,0.55)", amb: "rgba(0,140,255,0.12)" } },
  { id: "robot-neon", label: "Neon Mech", rgb: "255 60 180", bg: "20 6 15", hex: "#FF3CB4",
    robot: { base: "#14060f", line: "rgba(255,40,160,0.05)", line2: "rgba(255,40,160,0.12)", glow: "rgba(255,70,190,0.55)", amb: "rgba(255,0,140,0.12)" } },
  { id: "robot-matrix", label: "Matrix Circuit", rgb: "0 255 140", bg: "4 18 10", hex: "#00FF8C",
    robot: { base: "#04120a", line: "rgba(0,255,140,0.05)", line2: "rgba(0,255,140,0.12)", glow: "rgba(0,255,150,0.55)", amb: "rgba(0,200,110,0.12)" } },
  { id: "robot-reactor", label: "Reactor Core", rgb: "255 160 40", bg: "24 15 3", hex: "#FFA028",
    robot: { base: "#180f03", line: "rgba(255,160,40,0.05)", line2: "rgba(255,160,40,0.12)", glow: "rgba(255,180,60,0.55)", amb: "rgba(255,120,0,0.12)" } },
  { id: "flow-aurora", label: "Aurora Steel", rgb: "0 230 200", bg: "6 18 24", hex: "#00E6C8",
    anim: { base: "#06121a", edge: "rgba(120,90,255,0.16)" } },
  { id: "flow-sunset", label: "Sunset Steel", rgb: "255 120 70", bg: "24 10 6", hex: "#FF7846",
    anim: { base: "#180806", edge: "rgba(255,40,120,0.16)" } },
  { id: "flow-plasma", label: "Plasma Steel", rgb: "200 100 255", bg: "18 6 34", hex: "#C864FF",
    anim: { base: "#100620", edge: "rgba(0,180,255,0.16)" } },
  { id: "flow-toxic", label: "Toxic Steel", rgb: "150 255 60", bg: "10 22 4", hex: "#96FF3C",
    anim: { base: "#0a1604", edge: "rgba(0,210,255,0.14)" } },
  { id: "flow-chrome", label: "Chrome Steel", rgb: "200 212 230", bg: "16 18 24", hex: "#C8D4E6",
    anim: { base: "#12141c", edge: "rgba(180,200,255,0.16)" } },
  { id: "flow-gold", label: "Gold Steel", rgb: "255 200 60", bg: "24 18 4", hex: "#FFC83C",
    anim: { base: "#1a1404", edge: "rgba(255,120,0,0.18)" } },
  { id: "flow-ice", label: "Ice Steel", rgb: "150 220 255", bg: "8 18 28", hex: "#96DCFF",
    anim: { base: "#08121c", edge: "rgba(0,200,255,0.18)" } },
  { id: "flow-rose", label: "Rose Steel", rgb: "255 150 175", bg: "26 12 16", hex: "#FF96AF",
    anim: { base: "#1a0c10", edge: "rgba(255,60,120,0.18)" } },
];

const KEY = "aw-accent";

export const getAccent = () => {
  try {
    const saved = localStorage.getItem(KEY);
    return ACCENTS.some((a) => a.id === saved) ? saved : "black";
  } catch (_) { return "black"; }
};

export const applyAccent = (id) => {
  const a = ACCENTS.find((x) => x.id === id) || ACCENTS[0];
  const root = document.documentElement;
  root.style.setProperty("--ac-rgb", a.rgb);
  root.style.setProperty("--bg-rgb", a.bg);
  root.classList.toggle("theme-light", !!a.light);
  root.classList.toggle("theme-camo", !!a.camo);
  root.classList.toggle("theme-robot", !!a.robot);
  root.classList.toggle("theme-anim", !!a.anim);
  if (a.camo) {
    root.style.setProperty("--camo-base", a.camo.base);
    root.style.setProperty("--camo-a", a.camo.a);
    root.style.setProperty("--camo-b", a.camo.b);
    root.style.setProperty("--camo-c", a.camo.c);
    root.style.setProperty("--camo-d", a.camo.d);
  }
  if (a.robot) {
    root.style.setProperty("--robot-base", a.robot.base);
    root.style.setProperty("--robot-line", a.robot.line);
    root.style.setProperty("--robot-line2", a.robot.line2);
    root.style.setProperty("--robot-glow", a.robot.glow);
    root.style.setProperty("--robot-amb", a.robot.amb);
  }
  if (a.anim) {
    root.style.setProperty("--anim-base", a.anim.base);
    root.style.setProperty("--anim-edge", a.anim.edge);
  }
  try { localStorage.setItem(KEY, a.id); } catch (_) {}
  window.dispatchEvent(new CustomEvent("accent-change", { detail: a.id }));
  return a.id;
};

// Next id in the cycle (wraps back to Black after the last).
export const nextAccentId = (id) => {
  const i = ACCENTS.findIndex((x) => x.id === id);
  return ACCENTS[(i + 1) % ACCENTS.length].id;
};
